import { useState } from 'react';
import { useSales } from '@/hooks/use-sales';
import { useI18nContext } from '@/i18n/i18n-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Terminal } from 'lucide-react';
import { DateTime } from 'luxon'; // Keep DateTime for consistent date formatting
import { SaleItem } from '@/lib/dtos';
import { Button } from '@/components/ui/button';
import { useNavigate, useParams } from 'react-router';
import { getLuxonLocale } from '@/lib/utils';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { NotificationPreview } from '@/components/notification-preview';
import { useTenant } from '@/hooks/use-tenant';
import { SalesFilters } from '@/components/sales-filters';
import { SALES_DESKTOP_PAGE_SIZE } from '@/lib/sales-history';
import type { SalesHistoryFilters } from '@/components/sales-history/use-sales-history-filters';

/**
 * Desktop sales history (SALES-07 AC 3): the Vendas inline filters, table
 * and pagination, moved from `sales.page.tsx` without changing them.
 *
 * SPEC_DEVIATION: design §11 lists `data`/`isLoading`/`isError`/`error`/
 * `refetch`/`onPageChange` as props; the table runs `useSales` itself.
 * Reason: like `SalesHistoryList`, it is only mounted when it is shown, so
 * Pedidos never requests the table page on mobile or behind the
 * integration lock; the page comes from `filters.page`/`filters.setPage`.
 */
export function SalesHistoryTable({
  filters,
}: {
  filters: SalesHistoryFilters;
}) {
  const { LL, locale } = useI18nContext();
  const tenant = useTenant();
  const navigate = useNavigate();
  const tenantSlug = useParams().tenantSlug ?? '';
  const currentPage = filters.page;

  const {
    data: paginatedSales,
    refetch,
    isLoading,
    error,
    isError,
  } = useSales(currentPage, SALES_DESKTOP_PAGE_SIZE, filters.values);

  const handlePageChange = (page: number) => {
    filters.setPage(page);
  };

  const getFormattedPaymentStatus = (status: string) => {
    switch (status) {
      case 'paid':
        return LL.salesPage.filters.paid();
      case 'unpaid':
        return LL.salesPage.filters.unpaid();
      default:
        return status;
    }
  };

  const canShowPiiColumns = tenant.hasAccessToPii !== false;

  const baseColumns: ColumnDef<SaleItem>[] = [
    {
      accessorKey: 'date',
      header: LL.salesPage.table.date(),
      cell: ({ row }) => (
        <div className="w-max">
          {DateTime.fromISO(row.getValue('date'))
            .setLocale(getLuxonLocale(locale))
            .toLocaleString(DateTime.DATETIME_SHORT)}{' '}
          -{' '}
          {DateTime.fromISO(row.getValue('date'))
            .setLocale(getLuxonLocale(locale))
            .toRelative()}
        </div>
      ),
    },

    {
      accessorKey: 'orderNumber',
      header: LL.salesPage.table.orderNumber(),
      cell: ({ row }) => (
        <div className="w-max min-w-[150px]">{row.getValue('orderNumber')}</div>
      ),
    },
    {
      accessorKey: 'amount',
      header: () => (
        <div className="text-right">{LL.salesPage.table.amount()}</div>
      ),
      cell: ({ row }) => {
        const order = row.original;
        const amount = parseFloat(row.getValue('amount'));
        const formatted = new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: order.currency,
        }).format(amount);
        return <div className="text-right font-medium">{formatted}</div>;
      },
    },
    {
      accessorKey: 'paymentStatus',
      header: LL.salesPage.table.situation(),
      cell: ({ row }) => (
        <div className="w-max">
          {getFormattedPaymentStatus(row.getValue('paymentStatus'))}
        </div>
      ),
    },
    {
      accessorKey: 'productNames',
      header: LL.salesPage.table.product(),
      cell: ({ row }) => {
        const order = row.original;
        return (
          <div className="flex flex-col gap-2 w-max">
            {order.lineItemNames.map((productName) => (
              <span>{productName}</span>
            ))}
          </div>
        );
      },
    },
    {
      accessorKey: 'productQtt',
      header: LL.salesPage.table.quantity(),
      cell: ({ row }) => {
        const order = row.original;
        return (
          <div className="flex flex-col gap-2">
            {order.lineItemQuantities.map((qtt) => (
              <span>{qtt}</span>
            ))}
          </div>
        );
      },
    },
    {
      accessorKey: 'productSkus',
      header: LL.salesPage.table.sku(),
      cell: ({ row }) => {
        const order = row.original;
        return (
          <div className="flex flex-col gap-2 w-max">
            {order.lineItemSkus.map((sku) => (
              <span>{sku}</span>
            ))}
          </div>
        );
      },
    },
    {
      accessorKey: 'campaignName',
      header: LL.salesPage.table.campaign(),
      cell: ({ row }) => {
        const order = row.original;
        return order.campaignName ? (
          <div className="w-max">
            <HoverCard>
              <HoverCardTrigger asChild>
                <Button
                  onClick={() =>
                    navigate(`/${tenantSlug}/campaigns/${order.campaignId}`)
                  }
                  variant="link"
                >
                  {order.campaignName}
                </Button>
              </HoverCardTrigger>
              <HoverCardContent className="w-[400px]">
                <div className="flex flex-col gap-2 w-full">
                  <NotificationPreview
                    title={order.campaignNotificationTitle ?? ''}
                    body={order.campaignNotificationBody ?? ''}
                    iconUrl={tenant.iconUrl}
                  />
                  <Button
                    size={'sm'}
                    variant="outline"
                    onClick={() =>
                      navigate(`/${tenantSlug}/campaigns/${order.campaignId}`)
                    }
                  >
                    {LL.salesPage.table.seeCampaignDetails()}
                  </Button>
                </div>
              </HoverCardContent>
            </HoverCard>
          </div>
        ) : (
          ''
        );
      },
    },
    {
      accessorKey: 'utm',
      header: LL.salesPage.table.utmTag(),
      cell: ({ row }) => {
        const order = row.original;
        return (
          <div className="whitespace-nowrap overflow-hidden text-ellipsis w-max">
            utm_source={order.utmSource},utm_campaign={order.utmCampaign}
          </div>
        );
      },
    },
  ];
  const piiColumns: ColumnDef<SaleItem>[] = [
    {
      accessorKey: 'customerName',
      header: LL.salesPage.table.customerName(),
      cell: ({ row }) => (
        <div className="w-max min-w-[150px]">
          {row.getValue('customerName')}
        </div>
      ),
    },
    {
      accessorKey: 'customerPhone',
      header: LL.salesPage.table.customerPhone(),
      cell: ({ row }) => (
        <div className="w-max min-w-[150px]">
          {row.getValue('customerPhone')}
        </div>
      ),
    },
    {
      accessorKey: 'customerEmail',
      header: LL.salesPage.table.customerEmail(),
      cell: ({ row }) => (
        <div className="w-max min-w-[150px]">
          {row.getValue('customerEmail')}
        </div>
      ),
    },
  ];
  const columns: ColumnDef<SaleItem>[] = canShowPiiColumns
    ? [...baseColumns, ...piiColumns]
    : baseColumns;

  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const renderPaginationItems = () => {
    if (!paginatedSales || paginatedSales.totalPages <= 1) return null;

    const items = [];
    const totalPages = paginatedSales.totalPages;

    items.push(
      <PaginationItem key="prev">
        <PaginationPrevious
          href="#"
          onClick={(e) => {
            e.preventDefault();
            if (currentPage > 1) handlePageChange(currentPage - 1);
          }}
          className={currentPage === 1 ? 'pointer-events-none opacity-50' : ''}
        />
      </PaginationItem>
    );

    let startPage = Math.max(1, currentPage - 1);
    let endPage = Math.min(totalPages, currentPage + 1);

    if (currentPage === 1) {
      endPage = Math.min(totalPages, 3);
    }
    if (currentPage === totalPages && totalPages > 2) {
      startPage = Math.max(1, totalPages - 2);
    }

    if (startPage > 1) {
      items.push(
        <PaginationItem key={1}>
          <PaginationLink
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handlePageChange(1);
            }}
          >
            1
          </PaginationLink>
        </PaginationItem>
      );
      if (startPage > 2) {
        items.push(<PaginationEllipsis key="start-ellipsis" />);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <PaginationItem key={i}>
          <PaginationLink
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handlePageChange(i);
            }}
            isActive={i === currentPage}
          >
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<PaginationEllipsis key="end-ellipsis" />);
      }
      items.push(
        <PaginationItem key={totalPages}>
          <PaginationLink
            href="#"
            onClick={(e) => {
              e.preventDefault();
              handlePageChange(totalPages);
            }}
          >
            {totalPages}
          </PaginationLink>
        </PaginationItem>
      );
    }

    items.push(
      <PaginationItem key="next">
        <PaginationNext
          href="#"
          onClick={(e) => {
            e.preventDefault();
            if (currentPage < totalPages) handlePageChange(currentPage + 1);
          }}
          className={
            currentPage === totalPages ? 'pointer-events-none opacity-50' : ''
          }
        />
      </PaginationItem>
    );
    return items;
  };

  const table = useReactTable({
    data: paginatedSales?.data || [],
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      columnFilters,
    },
  });

  return (
    <>
      <SalesFilters
        campaignsFilter={filters.campaignsFilter}
        setCampaignsFilter={filters.setCampaignsFilter}
        fromDateFilter={filters.fromDateFilter}
        setFromDateFilter={filters.setFromDateFilter}
        toDateFilter={filters.toDateFilter}
        setToDateFilter={filters.setToDateFilter}
        predefinedDateRange={filters.predefinedDateRange}
        setPredefinedDateRange={filters.setPredefinedDateRange}
        paymentStatusFilter={filters.paymentStatusFilter}
        setPaymentStatusFilter={filters.setPaymentStatusFilter}
        textFilter={filters.textFilter}
        setTextFilter={filters.setTextFilter}
        clearFilters={filters.clearFilters}
        refetch={refetch}
        isLoading={isLoading}
      />

      {isError && (
        <Alert variant="destructive" className="mb-4">
          <Terminal className="h-4 w-4" />
          <AlertTitle>{LL.general.error()}</AlertTitle>
          <AlertDescription>
            {error?.message || LL.salesPage.failedToLoadSalesData()}
          </AlertDescription>
        </Alert>
      )}

      {paginatedSales && paginatedSales.data.length >= 0 && (
        <>
          <div className="border-border border rounded-2xl overflow-hidden">
            <Table containerClassName="overflow-scroll h-full">
              <TableHeader className="sticky top-0 bg-background">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      return (
                        <TableHead key={header.id}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && 'selected'}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      {LL.salesPage.table.noResults()}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          {paginatedSales.totalPages > 1 && (
            <Pagination className="mt-4">
              <PaginationContent>{renderPaginationItems()}</PaginationContent>
            </Pagination>
          )}
        </>
      )}
    </>
  );
}
